/**
 * Toast Notification Component
 * 
 * Provides user feedback for success, error, warning, and info messages
 * Follows Material Design and Apple HIG patterns
 */

import React, { useEffect, useCallback, useRef } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastProps {
  toast: Toast;
  onClose: (id: string) => void;
}

const ToastComponent: React.FC<ToastProps> = ({ toast, onClose }) => {
  // Use a ref for onClose so the timer effect never needs to re-run when the
  // callback reference changes between renders (which would reset the timer)
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    const duration = toast.duration || 5000;
    const timer = setTimeout(() => {
      onCloseRef.current(toast.id);
    }, duration);

    return () => clearTimeout(timer);
    // Only re-run if the toast id or duration changes, NOT when onClose changes
  }, [toast.id, toast.duration]);

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return 'ℹ️';
    }
  };

  const getStyles = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  return (
    <div
      role="alert"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      className={`${getStyles()} border rounded-lg shadow-lg p-4 mb-2 flex items-center gap-3 min-w-[300px] max-w-[500px] animate-slide-in`}
    >
      <span className="text-xl flex-shrink-0" aria-hidden="true">
        {getIcon()}
      </span>
      <p className="flex-1 text-sm font-medium">{toast.message}</p>
      <button
        onClick={() => onClose(toast.id)}
        className="flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 rounded"
        aria-label="Sluit notificatie"
      >
        <span className="sr-only">Sluiten</span>
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onClose: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onClose }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-[9999] flex flex-col items-end"
      role="region"
      aria-label="Notificaties"
    >
      {toasts.map((toast) => (
        <ToastComponent key={toast.id} toast={toast} onClose={onClose} />
      ))}
    </div>
  );
};

// Hook for using toast notifications
export const useToast = () => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((
    message: string,
    type: ToastType = 'info',
    duration?: number
  ) => {
    // Deduplication: don't add a toast if an identical message+type is already visible
    let existing = false;
    setToasts((prev) => {
      if (prev.some((t) => t.message === message && t.type === type)) {
        existing = true;
        return prev;
      }
      const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      return [...prev, { id, message, type, duration }];
    });
    if (existing) return '';
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    return id;
  }, []);

  const success = useCallback((message: string, duration?: number) =>
    showToast(message, 'success', duration), [showToast]);
  const error = useCallback((message: string, duration?: number) =>
    showToast(message, 'error', duration), [showToast]);
  const warning = useCallback((message: string, duration?: number) =>
    showToast(message, 'warning', duration), [showToast]);
  const info = useCallback((message: string, duration?: number) =>
    showToast(message, 'info', duration), [showToast]);

  return {
    toasts,
    showToast,
    removeToast,
    success,
    error,
    warning,
    info,
    ToastContainer: () => <ToastContainer toasts={toasts} onClose={removeToast} />,
  };
};
